import { useLocalPersistedState } from '@neo.tax/shared/hooks';
import { LocalPersistenceKey } from '@neo.tax/shared/types';

import { ColumnDefinition } from '../../filter-popover';
import { makeColumnVisibilitySchema, type ColumnVisibilityState } from '../column-visibility-popover.types';

type ColumnVisibilityProps<ColumnName extends string> = {
  columns: ColumnDefinition<ColumnName>[] | readonly ColumnDefinition<ColumnName>[];
  alwaysVisibleColumns: ColumnName[];
  initialVisibleColumns: ColumnName[];
  persistenceKey?: LocalPersistenceKey;
};

export const useColumnVisibility = <ColumnName extends string>({
  alwaysVisibleColumns,
  columns,
  initialVisibleColumns,
  persistenceKey,
}: ColumnVisibilityProps<ColumnName>): ColumnVisibilityState<ColumnName> => {
  // Storing visible columns state in an array for two reasons:
  // 1. It's easier to work with than a Set
  // 2. Allows us to define column order if we want in the future
  const [visibleColumns, setVisibleColumns] = useLocalPersistedState({
    dataSchema: makeColumnVisibilitySchema(columns.map((c) => c.name)),
    // Use Set to guarantee column uniqueness
    initialData: Array.from(new Set([...alwaysVisibleColumns, ...initialVisibleColumns])),
    persistenceKey: persistenceKey ? `columnVisibility:${persistenceKey}` : null,
  });

  const onColumnVisibilityChange = (visibleColumns: ColumnName[]): void => {
    // Use Set to guarantee column uniqueness
    setVisibleColumns(Array.from(new Set([...alwaysVisibleColumns, ...visibleColumns])));
  };

  return {
    columns,
    alwaysVisibleColumns,
    visibleColumns,
    setVisibleColumns,
    onColumnVisibilityChange,
  };
};
